import axios from 'axios';
import { createCloudRunAxios } from './axios';

const remiApi = createCloudRunAxios('remi-api');

/**
 * Organization Secrets API
 *
 * Used during onboarding to store sensitive credentials without putting them
 * directly on the organization document.
 */

// ============================================
// Types for Onboarding Secrets
// ============================================

export interface DomainRegistrarSecrets {
  registrar?: string;
  username?: string;
  password?: string;
  email?: string;
  phone?: string;
}

export interface CRMSecrets {
  crm_type: string;
  crm_other_name?: string;
  api_key?: string;
  login_url?: string;
  username?: string;
  password?: string;
  two_fa_contact_type?: 'me' | 'other';
  two_fa_contact_name?: string;
  two_fa_contact_email?: string;
  two_fa_contact_phone?: string;
  // Brivity specific
  brivity_website_url?: string;
  brivity_username?: string;
  brivity_password?: string;
}

export interface OnboardingSecretsPayload {
  private: {
    // Stage 2 - Website Branding domain access
    domain_registrar?: DomainRegistrarSecrets;
    // Stage 4 - email domain provider access (Remi Email Domain Setup)
    domain_registrar_idx?: DomainRegistrarSecrets;
    // Stage 4 - Remi's IDX domain provider access (IDX DNS setup)
    domain_registrar_remi_idx?: DomainRegistrarSecrets;
    // Stage 4 - Business Tax ID
    business?: {
      ein_cbn?: string;
    };
    // Stage 5 - CRM credentials (array for multiple CRMs)
    crm?: CRMSecrets[];
    // CRMs connected through Speculo's Connect form, keyed by CRM so each save
    // merges in without replacing the others
    crm_connect?: Record<string, Record<string, string | undefined>>;
  };
}

// ============================================
// API Functions
// ============================================

/**
 * Save onboarding secrets (domain registrar, CRM credentials, etc.)
 * This stores sensitive data in Secret Manager instead of Firestore.
 *
 * @param orgId - Organization ID
 * @param secrets - The secrets payload
 */
export const saveOnboardingSecrets = async (
  orgId: string,
  secrets: OnboardingSecretsPayload
): Promise<{ success: boolean }> => {
  try {
    const response = await remiApi.post(`/api/v1/organizations/${orgId}/secrets`, secrets);
    return response.data;
  } catch (error) {
    console.error('Error saving onboarding secrets:', error);
    throw error;
  }
};

/**
 * Save Stage 2 domain registrar secrets (Website Branding)
 */
export const saveDomainRegistrarSecrets = async (
  orgId: string,
  data: {
    domain_registrar?: string;
    registrar_username?: string;
    registrar_password?: string;
    registrar_email?: string;
    registrar_phone?: string;
  }
): Promise<{ success: boolean }> => {
  const secrets: OnboardingSecretsPayload = {
    private: {
      domain_registrar: {
        registrar: data.domain_registrar,
        username: data.registrar_username,
        password: data.registrar_password,
        email: data.registrar_email,
        phone: data.registrar_phone,
      },
    },
  };
  return saveOnboardingSecrets(orgId, secrets);
};

/**
 * Save Stage 4 IDX domain registrar secrets (if different from Stage 2)
 */
export const saveIDXDomainRegistrarSecrets = async (
  orgId: string,
  data: {
    domain_registrar?: string;
    registrar_username?: string;
    registrar_password?: string;
    registrar_email?: string;
    registrar_phone?: string;
  }
): Promise<{ success: boolean }> => {
  const secrets: OnboardingSecretsPayload = {
    private: {
      domain_registrar_idx: {
        registrar: data.domain_registrar,
        username: data.registrar_username,
        password: data.registrar_password,
        email: data.registrar_email,
        phone: data.registrar_phone,
      },
    },
  };
  return saveOnboardingSecrets(orgId, secrets);
};

/**
 * Save Stage 4 Remi's IDX domain provider login, so Speculo can add the IDX DNS record
 */
export const saveRemiIdxDomainRegistrarSecrets = async (
  orgId: string,
  data: {
    domain_registrar?: string;
    registrar_username?: string;
    registrar_password?: string;
    registrar_email?: string;
    registrar_phone?: string;
  }
): Promise<{ success: boolean }> =>
  saveOnboardingSecrets(orgId, {
    private: {
      domain_registrar_remi_idx: {
        registrar: data.domain_registrar,
        username: data.registrar_username,
        password: data.registrar_password,
        email: data.registrar_email,
        phone: data.registrar_phone,
      },
    },
  });

/**
 * Save the details a client entered in Speculo's Connect form for a CRM
 * (API key or login), so the team can finish the connection.
 */
export const saveCrmConnectSecrets = async (
  orgId: string,
  crm: string,
  values: Record<string, string | undefined>
): Promise<{ success: boolean }> =>
  saveOnboardingSecrets(orgId, {
    private: {
      crm_connect: { [crm]: { ...values, submitted_at: new Date().toISOString() } },
    },
  });

/**
 * Save Stage 4 business verification secrets (EIN/CBN)
 */
export const saveBusinessSecrets = async (
  orgId: string,
  data: {
    ein_cbn?: string;
  }
): Promise<{ success: boolean }> => {
  const secrets: OnboardingSecretsPayload = {
    private: {
      business: {
        ein_cbn: data.ein_cbn,
      },
    },
  };
  return saveOnboardingSecrets(orgId, secrets);
};

/**
 * Save Stage 5 CRM credentials
 * This handles multiple CRM entries
 */
export const saveCRMSecrets = async (
  orgId: string,
  crmEntries: Array<{
    crm_type: string;
    crm_other_name?: string;
    super_admin_api_key?: string;
    login_url?: string;
    super_admin_username?: string;
    super_admin_password?: string;
    two_fa_contact_type?: 'me' | 'other';
    two_fa_contact_name?: string;
    two_fa_contact_email?: string;
    two_fa_contact_phone?: string;
    brivity_website_login_url?: string;
    brivity_username?: string;
    brivity_password?: string;
  }>
): Promise<{ success: boolean }> => {
  const crmSecrets: CRMSecrets[] = crmEntries.map((entry) => ({
    crm_type: entry.crm_type,
    crm_other_name: entry.crm_other_name,
    api_key: entry.super_admin_api_key,
    login_url: entry.login_url,
    username: entry.super_admin_username,
    password: entry.super_admin_password,
    two_fa_contact_type: entry.two_fa_contact_type,
    two_fa_contact_name: entry.two_fa_contact_name,
    two_fa_contact_email: entry.two_fa_contact_email,
    two_fa_contact_phone: entry.two_fa_contact_phone,
    brivity_website_url: entry.brivity_website_login_url,
    brivity_username: entry.brivity_username,
    brivity_password: entry.brivity_password,
  }));

  const secrets: OnboardingSecretsPayload = {
    private: {
      crm: crmSecrets,
    },
  };
  return saveOnboardingSecrets(orgId, secrets);
};

/**
 * Get organization secrets (public tier only for frontend)
 */
export const getOrganizationSecrets = async (
  orgId: string
): Promise<{
  public?: Record<string, unknown>;
  lastUpdated?: string;
}> => {
  try {
    const response = await remiApi.get(`/api/v1/organizations/${orgId}/secrets`);
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.status === 404) {
      return { public: {} };
    }
    console.error('Error getting organization secrets:', error);
    throw error;
  }
};
